import { Box, chakra, Flex, Text, useToast } from "@chakra-ui/react";
import { RiDraggable } from "@remixicon/react";
import { CellContext, Row } from "@tanstack/react-table";
import { useId } from "react";
import { useDrag, useDrop } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { useParams } from "react-router";

import { ErrorResponseDisplay } from "@/common/components/ErrorResponseDisplay";
import { TreeVisualization } from "@/common/components/TreeVisualization";
import { useSubsegmentParam } from "@/common/hooks/useParam";
import { DragType } from "@/common/types/dragDropTypes";
import { IndentType } from "@/common/utils/tree";
import {
  FeatureDisplay,
  FeatureIndicatorsDisplay,
} from "@/feature/components/FeatureDisplay";
import RowExpanderButton from "@/feature/components/RowExpanderButton";
import { useFeatureNamesData } from "@/feature/data/useFeatureNamesData";
import { useFeatureUpdateMutation } from "@/feature/data/useFeatureUpdateMutation";
import { FeatureName } from "@reflag/shared/featureAPI";
import { getFeatureParentNames } from "@reflag/shared/utils/getFeatureParentNames";

const DraggableIcon = chakra(RiDraggable, {
  baseStyle: {
    boxSize: 3,
    color: "dimmed",
  },
});

export function FeatureNameCell<TRow extends FeatureName>({
  row,
  table,
}: CellContext<TRow, string>) {
  // Check if this is actually a grouped row to be more precise
  const isGroupedRow = row.getIsGrouped();
  const featureDisplayId = useId();

  const [subsegment] = useSubsegmentParam();

  const toast = useToast();
  const { companyId } = useParams();
  const featureUpdateMutation = useFeatureUpdateMutation(companyId!);

  const { data: featureNames = [] } = useFeatureNamesData();

  const [{ canDrag }, drag, preview] = useDrag<
    FeatureName,
    void,
    { canDrag: boolean }
  >(
    () => ({
      type: DragType.FeatureDisplay,
      item: () => row.original,
      // DON'T SET OPTIONS UNLESS ALSO USING A DEPENDENCY
      // SEE: https://github.com/react-dnd/react-dnd/issues/3345
      // options: {
      //   dropEffect: "copy",
      // },
      collect(monitor) {
        return {
          canDrag: monitor.canDrag(),
        };
      },
    }),
    [row.original],
  );

  const [{ isOver, canDrop }, drop] = useDrop<
    FeatureName,
    void,
    { isOver: boolean; canDrop: boolean }
  >(
    () => ({
      accept: DragType.FeatureDisplay,
      collect(monitor) {
        return {
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop(),
        };
      },
      canDrop(feature) {
        const dropFeature = row.original;

        // Don't attempt to re-set the current parent flag, or assign self as parent
        if ([feature.id, feature.parentFeatureId].includes(dropFeature.id)) {
          return false;
        }

        // Can't drop a flag on its own children
        const dropParents = getFeatureParentNames(featureNames, dropFeature.id);
        if (dropParents.some((p) => p.id === feature.id)) {
          return false;
        }

        return true;
      },
      drop(feature) {
        featureUpdateMutation.mutate(
          {
            id: feature.id,
            parentFeatureId: row.original.id,
          },
          {
            onSuccess() {
              toast({
                title: `Flag "${feature.name}" is now a child of "${row.original.name}"`,
                status: "success",
              });
            },
            onError(error) {
              toast({
                title: (
                  <Box>
                    <Text>{`Unable to move "${feature.name}" inside "${row.original.name}".`}</Text>
                    <ErrorResponseDisplay response={error.response?.data} />
                  </Box>
                ),
                status: "error",
              });
            },
          },
        );
      },
    }),
    [row.original, featureNames, featureUpdateMutation, toast],
  );

  const sortType = (table.options?.meta as any)?.sortType;
  if (sortType !== "hierarchical") {
    return (
      <Flex gap="2">
        <FeatureDisplay
          feature={row.original}
          flex="0 1 auto"
          id={featureDisplayId}
          subsegmentId={subsegment[0]}
          link
          showArchivedLabel
          onClick={(e) => e.stopPropagation()}
        />

        <Box flexGrow="1"></Box>

        <FeatureIndicatorsDisplay
          feature={row.original}
          showAutoFeedbackSurveyIndicator
          showStaleIndicator
        />
      </Flex>
    );
  }

  preview(getEmptyImage());

  // When the flag is a child in a tree, but is displayed as a root-node
  // because its parents are not a part of the same view; show the
  // parents names as well
  const shouldShowParentNames =
    row.original.parentFeatureId !== undefined &&
    row.getParentRow() === undefined;

  return (
    <Flex
      ref={drop}
      data-candrop={isOver && canDrop}
      flex="0 1 auto"
      ml={-6}
      my={-3.5}
      pl={6}
      position="relative"
      py={3}
      transition={"all 200ms ease-in-out"}
    >
      {/* Droppable indicator */}
      {canDrop && (
        <Box
          aria-hidden="true"
          background="currentColor"
          borderRadius="2px"
          h="10px"
          left={2}
          opacity={isOver ? "0.7" : "0.1"}
          pointerEvents="none"
          position="absolute"
          top="50%"
          transform="translateY(-50%)"
          transition="opacity 200ms"
          w="3px"
        ></Box>
      )}

      {/* Drag handle */}
      <Box
        ref={drag}
        background="appBackground"
        border="1px solid"
        borderColor="appBorder"
        cursor={canDrag ? "grab" : "pointer"}
        left={1}
        opacity="0"
        position="absolute"
        py={1}
        rounded="md"
        sx={{
          "&:active": {
            cursor: canDrag ? "grabbing" : "pointer",
          },
        }}
        top="50%"
        transform="translateY(-50%)"
        transition="opacity 150ms ease-in-out"
        zIndex="1"
        aria-hidden
        data-draghandle
      >
        <DraggableIcon />
      </Box>

      <TreeVisualization
        indents={getTreeVisualizationTypesFromTableRow(row, isGroupedRow)}
        my={-5}
      />

      <FeatureDisplay
        feature={row.original}
        flex="0 1 auto"
        id={featureDisplayId}
        includeParents={shouldShowParentNames}
        showArchivedLabel={false}
        subsegmentId={subsegment[0]}
        link
        onClick={(e) => e.stopPropagation()}
      />

      {/* Children expansion indicator  */}
      {row.getCanExpand() ? (
        <RowExpanderButton
          count={row.getLeafRows().length}
          isExpanded={row.getIsExpanded()}
          mx={2}
          onToggle={(e) => {
            e.stopPropagation();
            row.getToggleExpandedHandler()();
          }}
        />
      ) : null}

      <Box flexGrow="1" minW={4}></Box>

      <FeatureIndicatorsDisplay
        feature={row.original}
        showAutoFeedbackSurveyIndicator
        showStaleIndicator
      />
    </Flex>
  );
}

function getTreeVisualizationTypesFromTableRow<TData>(
  row: Row<TData>,
  isGroupedRow: boolean = false,
): Array<IndentType> {
  // Don't show tree visualization for grouped rows
  if (isGroupedRow) {
    return [];
  }

  const parentRows = row.getParentRows().slice(1);

  const result: Array<IndentType> = parentRows.map((p) => {
    if (hasNextSibling(p)) {
      return "│";
    } else {
      return ".";
    }
  });

  // Handle current row
  if (row.getParentRow()) {
    result.push(hasNextSibling(row) ? "├" : "└");
  }

  // Render a tree root if the children are expanded
  if (row.getCanExpand() && row.getIsExpanded()) {
    result.push("▽");
  }

  return result;
}

function hasNextSibling<TData>(row: Row<TData>): boolean {
  const parentRow = row.getParentRow();

  if (!parentRow) {
    return false;
  }

  const siblings = parentRow.subRows;

  if (siblings.indexOf(row) < siblings.length - 1) {
    return true;
  } else {
    return false;
  }
}
